import React, { memo, useEffect } from 'react';
import { Control, Controller, FieldPath, useWatch, useFormContext } from 'react-hook-form';
import { Spinner, TextInput } from '@tcc/crate';
import { Tooltip, ElementSide } from '@tcc/fds';
import { convertForLocale, inferUnit } from 'util/unitConversion';
import { convertPlantingPlanParams } from 'util/parse';
import { FormData, FieldData } from '../../types';
import { useFieldYieldData } from 'create/bulk/common/hooks/useFieldYieldData';
import { useAppSelector } from 'app/hooks';
import { getActiveCropDfpUuid } from 'current/selectors';
import { getNominalWeightByCropId } from 'crops/selectors';
import { getMessage } from 'util/locale';

interface TargetYieldInputCellProps {
  control: Control<FormData> | Control<Partial<FieldData>>;
  name: FieldPath<FormData> | Partial<keyof FieldData>;
  currency: string;
  rowIndex: number;
  fieldUuid: string;
  label?: string | undefined;
  children?: React.ReactNode;
  disabled?: boolean;
  showTooltip?: boolean;
}

export const TargetYieldInputCell = memo(function Cell({
  control,
  name,
  currency,
  rowIndex,
  fieldUuid,
  label,
  children,
  disabled,
  showTooltip,
}: TargetYieldInputCellProps) {
  const activeCropUuid = useAppSelector(getActiveCropDfpUuid);
  const cropNominalWeight = useAppSelector((state) => getNominalWeightByCropId(state, activeCropUuid));
  const { setValue } = useFormContext<FormData>();
  const watchedValue = useWatch({ control: control as Control<FormData>, name: name as FieldPath<FormData> });
  const { yieldData, isLoading, } = useFieldYieldData(fieldUuid, activeCropUuid);
  const averageYield = yieldData?.field?.harvestEventAggregates?.averageYield;
  const maximumYield = yieldData?.field?.harvestEventAggregates?.maximumYield;

  const convertedAvgYield = averageYield && convertForLocale({
    quantity: averageYield?.v,
    unit: averageYield?.u,
    unitType: 'area-density.yield',
    cropNominalWeight
  });

  const convertedMaxYield = maximumYield && convertForLocale({
    quantity: maximumYield?.v,
    unit: maximumYield?.u,
    unitType: 'area-density.yield',
    cropNominalWeight
  });

  useEffect(() => {
    if (convertedMaxYield && !isLoading && !watchedValue && watchedValue !== '') {
      setValue(name as FieldPath<FormData>, convertedMaxYield.scalarLocaleString, { shouldValidate: true });
    }
  }, [setValue, name, convertedMaxYield, isLoading, watchedValue]);

  const yieldTooltip = averageYield ? (
    <Tooltip testId='target-yield-input-tooltip' placement={ElementSide.Bottom}>
      <ul style={{ textAlign: 'end'}}>
        <li>{getMessage('target-yield-tooltip-highest', { highestYield: convertedMaxYield.toString() })}</li>
        <li>{getMessage('target-yield-tooltip-average', { avgYield: convertedAvgYield.toString() })}</li>
      </ul>
    </Tooltip>
  ) : null;

  return (
    <Controller
      control={control as Control<FormData>}
      name={name as FieldPath<FormData>}
      render={({ field, fieldState: { error } }) => {
        const { display } = convertPlantingPlanParams({
          validationResult: undefined,
          value: field.value,
          currency
        });

        const input = (
          <TextInput
            error={error?.message}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            placeholder={`0 ${inferUnit('area-density.yield').symbol.quantitySpecific}`}
            testId={`target-yield-input-${rowIndex}`}
            value={display}
            showReset={false}
            className="script-generation-row-input"
            label={label}
            disabled={disabled}
          >
            {!children && isLoading ? <Spinner size="20" className='yield-spinner' /> : yieldTooltip}
            {children}
          </TextInput>
        );

        if (showTooltip) {
          return (
            <Tooltip
              testId={`target-yield-locked-tooltip-${rowIndex}`}
              placement={ElementSide.Bottom}
              trigger={<span>{input}</span>}
            >
              {getMessage('bulk-fvss-script-generation-locked-input-tooltip')}
            </Tooltip>
          );
        }

        return input;
      }}
    />
  );
});
